import { X } from 'lucide-react';
import type { ReactNode, RefObject } from 'react';

import { cn } from '../../lib/utils';
import { Button } from './button';
import { ModalOverlay, modalSheetClassName } from './modal-overlay';
import { dialogTitleClassName } from './typography';
import { useModalDialog } from './use-modal-dialog';

export interface DialogFrameProps {
    open: boolean;
    title: string;
    description?: string;
    onClose: () => void;
    children: ReactNode;
    panelClassName?: string;
    headerAccessory?: ReactNode;
    suspended?: boolean;
    closeDisabled?: boolean;
    returnFocusRef?: RefObject<HTMLElement>;
}

export function DialogFrame({
    open,
    title,
    description,
    onClose,
    children,
    panelClassName,
    headerAccessory,
    suspended = false,
    closeDisabled = false,
    returnFocusRef,
}: DialogFrameProps) {
    const { titleId, descriptionId, panelProps } = useModalDialog({
        open,
        onClose,
        returnFocusRef,
    });
    if (!open) return null;

    const titleBlock = (
        <div>
            <h2 id={titleId} className={dialogTitleClassName}>
                {title}
            </h2>
            {description ? (
                <p id={descriptionId} className="mt-1 text-sm text-neutral-500">
                    {description}
                </p>
            ) : null}
        </div>
    );

    return (
        <ModalOverlay className={suspended ? 'hidden' : undefined}>
            <div
                {...panelProps}
                aria-describedby={description ? descriptionId : undefined}
                className={cn(
                    modalSheetClassName,
                    'flex max-h-[90vh] w-full max-w-2xl flex-col',
                    panelClassName,
                )}
            >
                <div className="flex shrink-0 items-start justify-between gap-4 border-b p-5">
                    {headerAccessory ? (
                        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                            {titleBlock}
                            {headerAccessory}
                        </div>
                    ) : (
                        titleBlock
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close dialog"
                        disabled={closeDisabled}
                        className="-m-1 flex size-9 shrink-0 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:pointer-events-none disabled:opacity-50"
                    >
                        <X className="size-5" aria-hidden />
                    </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>
            </div>
        </ModalOverlay>
    );
}

export const dialogFooterClassName =
    'sticky -bottom-5 z-10 -mx-5 !-mb-5 !mt-4 flex flex-wrap justify-end gap-2 border-t border-neutral-100 bg-white px-5 py-3';

export function DialogFooter({ children, className }: { children: ReactNode; className?: string }) {
    return <div className={cn(dialogFooterClassName, className)}>{children}</div>;
}

export function Actions({
    children,
    onCancel,
    status,
    statusId,
}: {
    children?: ReactNode;
    onCancel: () => void;
    status?: ReactNode;
    statusId?: string;
}) {
    return (
        <div className={dialogFooterClassName}>
            {status ? (
                <p
                    id={statusId}
                    className="basis-full text-xs text-neutral-600 sm:mr-auto sm:basis-auto sm:self-center"
                >
                    {status}
                </p>
            ) : null}
            <Button
                type="button"
                variant="outline"
                className="border-neutral-300 bg-white text-neutral-900 hover:bg-neutral-50"
                onClick={onCancel}
            >
                Cancel
            </Button>
            {children}
        </div>
    );
}
